import { useState, useCallback, useMemo } from 'react';
import { Plus, Trash2, Store, Mail, Package, Send } from 'lucide-react';
import { EntityType } from '@peles/shared';
import {
  useEntityLinks,
  useCreateEntityLink,
  useDeleteEntityLink,
  useUpdateEntityLink,
} from '../../hooks/useEntityLinks';
import { useEventVendorRecipients } from '../../hooks/useEvents';
import { SearchableSelect } from '../common/SearchableSelect';
import { AttachmentButton } from '../attachments/AttachmentButton';
import { SendVendorListModal } from './SendVendorListModal';
import { ComposeVendorEmailModal } from './ComposeVendorEmailModal';
import { VendorQuickEditModal } from './VendorQuickEditModal';
import { ItemizedListModal } from '../planning/ItemizedListModal';
import { useDialog } from '../../context/DialogContext';

interface VendorLinkItem {
  linkId: string;
  vendorId: string;
  vendorName: string;
  description: string;
  sortOrder: number;
}

interface EventVendorListViewProps {
  eventId: string;
  eventName?: string;
  eventDate?: string;
  venueId?: string | null;
  currentUser?: { email: string; name: string };
}

export function EventVendorListView({
  eventId,
  eventName,
  eventDate,
  venueId,
  currentUser,
}: EventVendorListViewProps) {
  const { confirm } = useDialog();
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(new Set());
  const [showInventory, setShowInventory] = useState(false);
  const [newVendorId, setNewVendorId] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [quickVendorId, setQuickVendorId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<{
    linkId: string;
    value: string;
  } | null>(null);

  const { data: links = [], isLoading } = useEntityLinks({
    sourceEntityType: EntityType.EVENT,
    sourceEntityId: eventId,
    linkType: 'VENDOR',
  });
  const createLink = useCreateEntityLink();
  const deleteLink = useDeleteEntityLink();
  const updateLink = useUpdateEntityLink();
  const { data: recipients = [] } = useEventVendorRecipients(eventId);

  const recipientByVendorId = useMemo(
    () => new Map(recipients.map((recipient) => [recipient.vendorId, recipient])),
    [recipients],
  );
  const selectedRecipients = useMemo(
    () =>
      [...selectedVendorIds]
        .map((vendorId) => recipientByVendorId.get(vendorId))
        .filter((recipient): recipient is NonNullable<typeof recipient> => !!recipient?.email),
    [selectedVendorIds, recipientByVendorId],
  );

  const toggleVendorSelected = useCallback((vendorId: string) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  }, []);

  const emailableVendorIds = useMemo(
    () => recipients.filter((r) => r.email).map((r) => r.vendorId),
    [recipients],
  );
  const allEmailableSelected =
    emailableVendorIds.length > 0 && emailableVendorIds.every((id) => selectedVendorIds.has(id));

  const toggleSelectAll = useCallback(() => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        emailableVendorIds.length > 0 && emailableVendorIds.every((id) => next.has(id));
      if (allSelected) emailableVendorIds.forEach((id) => next.delete(id));
      else emailableVendorIds.forEach((id) => next.add(id));
      return next;
    });
  }, [emailableVendorIds]);

  const vendorItems: VendorLinkItem[] = useMemo(
    () =>
      links.map((link) => {
        const meta = (link.metadata || {}) as Record<string, unknown>;
        return {
          linkId: link.id,
          vendorId: link.targetEntityId,
          vendorName: link.targetEntityName || 'Unknown Vendor',
          description: (meta.description as string) || '',
          sortOrder: link.sortOrder,
        };
      }),
    [links],
  );

  const handleAdd = useCallback(() => {
    if (!newVendorId) return;
    const linkedVendorId = newVendorId;
    createLink.mutate(
      {
        sourceEntityType: EntityType.EVENT,
        sourceEntityId: eventId,
        targetEntityType: EntityType.VENDOR,
        targetEntityId: linkedVendorId,
        linkType: 'VENDOR',
        metadata: { description: newDescription.trim() },
      },
      {
        onSuccess: () => {
          setNewVendorId('');
          setNewDescription('');
          setShowAddForm(false);
        },
      },
    );
  }, [newVendorId, newDescription, eventId, createLink]);

  const handleRemove = useCallback(
    async (linkId: string) => {
      if (
        await confirm({
          title: 'Remove Vendor',
          message: 'Remove this vendor from the event?',
          confirmLabel: 'Remove',
        })
      ) {
        deleteLink.mutate(linkId);
      }
    },
    [deleteLink, confirm],
  );

  const handleSaveDescription = useCallback(
    (linkId: string, description: string) => {
      updateLink.mutate({ id: linkId, updates: { metadata: { description } } });
      setEditingDescription(null);
    },
    [updateLink],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900">Vendors ({vendorItems.length})</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInventory(true)}
            title="Open the event's itemized inventory list"
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="inventory-list-button"
          >
            <Package size={16} />
            Inventory List
          </button>
          <button
            onClick={() => setShowComposeModal(true)}
            disabled={selectedRecipients.length === 0}
            title={
              selectedRecipients.length === 0
                ? 'Select vendors with an email address first'
                : 'Compose an email to the selected vendors'
            }
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="email-vendors-button"
          >
            <Send size={16} />
            Email Vendors{selectedRecipients.length > 0 ? ` (${selectedRecipients.length})` : ''}
          </button>
          {currentUser && eventName && (
            <button
              onClick={() => setShowEmailModal(true)}
              title="Send vendor list by email"
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
            >
              <Mail size={16} />
              Send Email
            </button>
          )}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Vendor</label>
              <SearchableSelect
                entityType="vendor"
                value={newVendorId}
                onChange={(v) => setNewVendorId(typeof v === 'string' ? v : '')}
                placeholder="Search vendors..."
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Description / Notes</label>
              <input
                type="text"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="e.g. Catering, Photography..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowAddForm(false)}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={!newVendorId || createLink.isPending}
              className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : vendorItems.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <Store size={32} className="mx-auto mb-2 opacity-40" />
          <p className="text-sm">No vendors added to this event yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl overflow-hidden">
          {emailableVendorIds.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-gray-50">
              <input
                type="checkbox"
                checked={allEmailableSelected}
                onChange={toggleSelectAll}
                aria-label="Select all vendors with an email"
                title="Select all vendors with an email"
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                data-testid="vendor-select-all"
              />
              <span className="text-xs font-medium text-gray-500">
                Select all ({emailableVendorIds.length} emailable)
              </span>
            </div>
          )}
          {vendorItems.map((item) => {
            const recipient = recipientByVendorId.get(item.vendorId);
            const emailable = !!recipient?.email;
            return (
              <div
                key={item.linkId}
                className="flex items-center gap-3 px-4 py-2.5 bg-white hover:bg-gray-50 transition-colors"
              >
                {/* Email multi-select — only vendors with a resolvable email */}
                <input
                  type="checkbox"
                  checked={selectedVendorIds.has(item.vendorId)}
                  disabled={!emailable}
                  onChange={() => toggleVendorSelected(item.vendorId)}
                  title={
                    emailable
                      ? `Email ${recipient?.email}`
                      : 'No email on the vendor or its contacts'
                  }
                  aria-label={`Select ${item.vendorName} for email`}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-40 flex-shrink-0"
                  data-testid={`vendor-email-checkbox-${item.vendorId}`}
                />

                {/* Icon */}
                <div className="w-7 h-7 bg-primary-100 rounded-md flex items-center justify-center flex-shrink-0">
                  <Store size={13} className="text-primary-600" />
                </div>

                {/* Vendor name — opens the quick view/edit modal */}
                <button
                  onClick={() => setQuickVendorId(item.vendorId)}
                  title="View / edit vendor details"
                  className="text-sm font-medium text-gray-900 hover:text-primary-600 w-40 flex-shrink-0 truncate text-left"
                >
                  {item.vendorName}
                </button>

                {/* Inline description */}
                <div className="flex-1 min-w-0">
                  {editingDescription?.linkId === item.linkId ? (
                    <input
                      type="text"
                      value={editingDescription.value}
                      autoFocus
                      onChange={(e) =>
                        setEditingDescription({ linkId: item.linkId, value: e.target.value })
                      }
                      onBlur={() => handleSaveDescription(item.linkId, editingDescription.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter')
                          handleSaveDescription(item.linkId, editingDescription.value);
                        if (e.key === 'Escape') setEditingDescription(null);
                      }}
                      className="w-full text-xs px-1.5 py-0.5 border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                    />
                  ) : (
                    <button
                      onClick={() =>
                        setEditingDescription({ linkId: item.linkId, value: item.description })
                      }
                      className="w-full text-left text-xs text-gray-500 hover:text-gray-700 truncate"
                      title="Click to edit description"
                    >
                      {item.description || (
                        <span className="text-gray-300 italic">Add description…</span>
                      )}
                    </button>
                  )}
                </div>

                {/* Attachment button */}
                <AttachmentButton entityType="ENTITY_LINK" entityId={item.linkId} />

                {/* Delete */}
                <button
                  onClick={() => handleRemove(item.linkId)}
                  title="Remove vendor"
                  className="p-1 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ComposeVendorEmailModal
        open={showComposeModal}
        onClose={() => setShowComposeModal(false)}
        eventId={eventId}
        recipients={selectedRecipients}
      />

      {showEmailModal && currentUser && eventName && (
        <SendVendorListModal
          eventId={eventId}
          eventName={eventName}
          eventDate={eventDate ?? ''}
          venueId={venueId}
          currentUser={currentUser}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      <ItemizedListModal
        open={showInventory}
        onClose={() => setShowInventory(false)}
        eventId={eventId}
        title="Inventory List"
      />

      <VendorQuickEditModal
        vendorId={quickVendorId}
        open={!!quickVendorId}
        onClose={() => setQuickVendorId(null)}
      />
    </div>
  );
}
